import { SettingsItemImage } from '@elipssolution/harfang';
import { useSession as useNextAuthSession } from 'next-auth/react';
import { useCallback, useEffect, useState } from 'react';

import { useConfirmationDialogContext } from '../../../components/ConfirmationDialogProvider';
import SettingsDialogPage from '../../../components/SettingsDialogPage';
import { useSession } from '../../components/session/SessionContext';
import { ContactType } from '../../types/contact';
import { uploadFileWithFormData } from '../../utils/file';

type ContactImageProps = {
	contact: Pick<ContactType, 'id' | 'name' | 'hasPicture'>;
};

const ContactImage = ({ contact: { id: contactId, name: contactName, hasPicture } }: ContactImageProps) => {
	const { confirmAction } = useConfirmationDialogContext();

	const { data: sessionData } = useNextAuthSession();
	const { access_token } = sessionData ?? {};

	const { customerFile: sessionCustomerFile } = useSession();
	const { id: customerFileId } = sessionCustomerFile || {};

	const [picture, setPicture] = useState<File>();

	const handleChange = (value?: File) => {
		setPicture(value);

		if (customerFileId && access_token && value) {
			const uri = new URL(`/contact/images`, window.location.href);
			uri.searchParams.append('contactId', contactId);

			uploadFileWithFormData({
				accessToken: access_token,
				file: value,
				selectedCustomerFileId: customerFileId,
				uri,
			}).catch((error) => {
				throw error;
			});
		}
	};

	const deleteImage = useCallback(async () => {
		setPicture(undefined);

		if (!hasPicture) return;

		const isRemovalConfirmed = await confirmAction({
			title: `Êtes-vous sûr de vouloir supprimer l'image de profil de ${contactName} ?`,
			confirmButtonDetails: {
				color: 'error',
				label: 'Supprimer',
			},
		});

		if (!isRemovalConfirmed) return;

		if (customerFileId && access_token) {
			const headers = new Headers();
			headers.append('Authorization', `Bearer ${access_token}`);
			headers.append('customer-file-id', customerFileId);

			const result = await fetch(`/contact/images?contactId=${contactId}`, {
				method: 'DELETE',
				headers,
			});

			if (!result.ok) {
				throw new Error("Erreur lors de la suppression de l'image.");
			}
		}
	}, [confirmAction, contactName, hasPicture, customerFileId, access_token, contactId]);

	const handleDelete = () => {
		deleteImage().catch((error) => {
			throw error;
		});
	};

	// Fetch contact picture on page load
	useEffect(() => {
		if (hasPicture) {
			(async () => {
				const response = await fetch(`/contact/images?contactId=${contactId}`);
				if (response.ok) {
					setPicture((await response.blob()) as File);
				}
			})().catch((error) => {
				throw error;
			});
		}
	}, [contactId, hasPicture]);

	return (
		<SettingsDialogPage title={contactName}>
			<SettingsItemImage
				description="Photo du contact. La résolution optimale est 100x100."
				label="Photo"
				image={picture}
				onChange={handleChange}
				onDelete={handleDelete}
				previewHeight={150}
				previewWidth={150}
			/>
		</SettingsDialogPage>
	);
};

export default ContactImage;
